import { useLayoutEffect, useRef, useState } from 'preact/hooks';
import { useSwipeable } from 'react-swipeable';
import waitForTimers from 'wait-for-timers';
import { green500, red400, slate200 } from './colors';

export type SwipeAction = {
  onSwipe: (animationDuration: number) => 'dismiss' | void;
  text: string;
  color: 'gray' | 'red' | 'green';
};

export const getBgColor = (color: 'gray' | 'red' | 'green') => {
  return color === 'gray' ? slate200 : color === 'green' ? green500 : red400;
};

export function useSwipe(swipeAction?: SwipeAction) {
  const swipeContainerRef = useRef<HTMLElement>(null);
  const [isActionVisible, setActionVisible] = useState(false);
  const staticActionWidth = 100;
  const animationDuration = 300;
  const swipeAmount = useRef(0);
  const shouldAnimateSwipe = useRef(false);
  const [dismissStep, setDismissStep] = useState(0);

  const setSwipeParams = (amount: number, shouldAnimate: boolean) => {
    if (swipeAmount.current === amount && shouldAnimateSwipe.current === shouldAnimate)
      return;
    swipeAmount.current = amount;
    shouldAnimateSwipe.current = shouldAnimate;

    if (swipeAction) {
      Object.assign(swipeContainerRef.current!.style, getSwipeContainerStyles());
      Object.assign(
        (swipeContainerRef.current!.firstElementChild as HTMLElement).style,
        getSwipeButtonStyles()
      );
    }
  };

  const swipeHandlers = useSwipeable({
    onSwiped: (event) => {
      if (event.dir === 'Left') {
        if (event.absX > staticActionWidth * 2) {
          // console.log('take action!');
          setSwipeParams(0, true);
          setActionVisible(false);
          const action = swipeAction!.onSwipe(animationDuration);
          if (action === 'dismiss') {
            setDismissStep(1);
          }
          return;
        }
        if (event.absX > staticActionWidth) {
          // console.log('show option');
          setSwipeParams(staticActionWidth, true);
          setActionVisible(true);
          return;
        }
      }
      // console.log('reset');
      setSwipeParams(0, true);
      setActionVisible(false);
    },
    onSwiping: (event) => {
      if (event.dir === 'Left') {
        setSwipeParams(event.absX + (isActionVisible ? staticActionWidth : 0), false);
      } else if (isActionVisible && event.absX < staticActionWidth) {
        setSwipeParams(staticActionWidth - event.absX, false);
      } else {
        setSwipeParams(0, false);
      }
    },
  });

  useLayoutEffect(() => {
    if (dismissStep === 1) {
      return waitForTimers(['raf', 'raf'], () => setDismissStep(2));
    }
  }, [dismissStep]);

  const swipeDismissStyles = dismissStep
    ? {
        // Needed to actually cut off item as height is reduced
        overflow: 'hidden',
        // Needed to NOT cut off right side of swipe action area
        margin: '0 -1.5rem 0',
        padding: '0 1.5rem',
        // Animate height and opacity
        transition: ['height', 'opacity'].map(toTransition).join(','),
        height:
          dismissStep === 1
            ? `${swipeContainerRef.current!.offsetHeight}px`
            : dismissStep === 2
              ? `0px`
              : '',
        opacity: '0',
      }
    : {};

  function getSwipeContainerStyles() {
    return swipeAction
      ? {
          transform: `translateX(-${swipeAmount.current}px)`,
          transition: shouldAnimateSwipe.current ? toTransition('transform') : 'none',
        }
      : {};
  }

  function getSwipeButtonStyles() {
    return {
      width: `${swipeAmount.current}px`,
      background: getBgColor(swipeAction!.color),
      transition: shouldAnimateSwipe.current ? toTransition('width') : '',
      whiteSpace: 'nowrap',
    };
  }

  function toTransition(property: string) {
    return `${property} ${animationDuration}ms ease-out`;
  }

  function handleSwipeContainerClick(event: MouseEvent) {
    const distanceFromRight = window.innerWidth - event.clientX;
    if (distanceFromRight < swipeAmount.current) {
      setSwipeParams(0, true);
      const action = swipeAction!.onSwipe(animationDuration);
      if (action === 'dismiss') {
        setDismissStep(1);
      }
    }
  }

  return {
    swipeHandlers,
    swipeContainerRef,
    swipeDismissStyles,
    handleSwipeContainerClick,
    getSwipeContainerStyles,
    getSwipeButtonStyles,
  };
}
